import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Same-origin override for the canonical production host so auth cookies are
// first-party. See App.js for the full rationale.
const PROD_HOSTS = new Set(["frenchboard.ca", "www.frenchboard.ca"]);
const RUNTIME_HOST = typeof window !== "undefined" ? window.location.hostname : "";
const BACKEND_URL = PROD_HOSTS.has(RUNTIME_HOST)
  ? window.location.origin
  : process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // If we're entering via an invite link, let InviteLanding handle auth setup.
    // Otherwise AuthContext's auto /auth/me race wipes the user that InviteLanding sets.
    if (window.location.pathname.startsWith('/try/')) {
      setLoading(false);
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const demoToken = params.get('demo') || sessionStorage.getItem('demo_token');

    if (demoToken) {
      try {
        await axios.get(`${API_URL}/auth/demo-validate?token=${demoToken}`);
        sessionStorage.setItem('demo_token', demoToken);
        if (params.get('demo')) {
          params.delete('demo');
          const newUrl = window.location.pathname + (params.toString() ? `?${params}` : '');
          window.history.replaceState({}, '', newUrl);
        }
        setUser({ demo: true, name: 'Demo', role: 'demo' });
        setLoading(false);
        return;
      } catch {
        sessionStorage.removeItem('demo_token');
      }
    }

    try {
      const { data } = await axios.get(`${API_URL}/auth/me`);
      setUser(data);
    } catch {
      try {
        await axios.post(`${API_URL}/auth/refresh`);
        const { data } = await axios.get(`${API_URL}/auth/me`);
        setUser(data);
      } catch {
        setUser(false);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  async function login(email, password) {
    await axios.post(`${API_URL}/auth/login`, { email, password });
    const { data } = await axios.get(`${API_URL}/auth/me`);
    setUser(data);
    return data;
  }

  async function logout() {
    try { await axios.post(`${API_URL}/auth/logout`); } catch {}
    sessionStorage.removeItem('demo_token');
    setUser(false);
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
